import React, { useEffect, useMemo } from 'react';

import { getBlockIds, PerformanceType } from '@lyearn/lygo-core';
import { Box } from '@lyearn/ui';
import { HeaderState } from '@/components/EditorViewer/hooks/types';
import useLessonMainContent from '@/components/EditorViewer/hooks/useLessonMainContent';
import PageEditor from '@/components/PageEditor';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import useScrollProgress from '@/hooks/useScrollProgress';
import useWindowFocusInterval from '@/hooks/useWindowFocusInterval';
import { useArticle } from '@/modules/corporate/modules/article/articleContext';
import useArticleEditorViewProps from '@/modules/corporate/modules/article/hooks/useArticleEditorViewProps';
import useArticleFetchPerformance from '@/modules/corporate/modules/article/hooks/useArticleFetchPerformance';
import useSyncPerformance from '@/modules/corporate/modules/course/hooks/useSyncPerformance';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import { ResourceTypeEnum } from '@/types/schema';

import { EditorProps } from './types';

const dummySetHeaderState = (_: HeaderState) => {};
const LEARNING_TIME_PING_INTERVAL = 15000;

const Editor: React.FC<EditorProps> = (props) => {
  const {
    editorDoc,
    fetchingBlocks,
    bottomRef,
    editorContainerRef,
    articleId,
    articleContentId,
    articleContentLessonPageId,
    isPreviewing,
    articleContentCustomDuration,
    bannerRef,
    setHeaderState = dummySetHeaderState,
    setLessonDurationLeft,
  } = props;
  const isAdmin = useAdminRoute();
  const { journeySessionId, pauseAvtivity } = useArticle();

  const {
    onCreateEditorView,
    individualNodePerformanceCallback,
    addCurrentLessonTime,
    editorView,
  } = useSyncPerformance(
    ResourceTypeEnum.Article,
    editorContainerRef,
    articleId,
    articleContentId,
    articleContentLessonPageId,
    !isAdmin && !pauseAvtivity,
    journeySessionId,
  );

  const entityIds = useMemo(() => {
    return getBlockIds(editorView, PerformanceType.BLOCK_BASED);
  }, [editorView]);

  const { data: contentPerformance } = useArticleFetchPerformance(entityIds);

  const scrollResult = useScrollProgress(editorContainerRef?.current);

  const { isVisible: isBannerVisible } = useIntersectionObserver(
    bannerRef?.current ?? null,
    editorContainerRef?.current,
  );

  const { lessonDurationLeft, getNodePerformance, intervalCallback } = useLessonMainContent(
    articleContentCustomDuration ?? 0,
    scrollResult,
    isBannerVisible,
    setHeaderState,
    editorDoc,
    addCurrentLessonTime,
    contentPerformance,
  );

  useEffect(() => {
    setLessonDurationLeft?.(lessonDurationLeft);
  }, [lessonDurationLeft, setLessonDurationLeft]);

  useWindowFocusInterval(intervalCallback, LEARNING_TIME_PING_INTERVAL);
  const articleEditorViewProps = useArticleEditorViewProps();

  return (
    <React.Suspense fallback={null}>
      <PageEditor
        {...articleEditorViewProps}
        editable={false}
        getNodePerformance={getNodePerformance}
        jsonDoc={editorDoc}
        loading={fetchingBlocks}
        setNodePerformance={individualNodePerformanceCallback}
        onCreateEditorView={onCreateEditorView}
      />
      <Box ref={bottomRef} className="h-64 w-full" />
    </React.Suspense>
  );
};

export default Editor;
